/**
 * Composable for loading a single agent's data via controller RPC.
 * Returns the full agent object with all fields (name, key, notes, history).
 */

import type { Node as ProseMirrorNode } from "prosemirror-model";
import { computed, provide, ref, shallowRef, watch, type Ref } from "vue";

import type {
  AiAgent,
  AiConversationRichResponse,
  AiPrompt,
  AiPromptContext,
  Attachment,
  Controller,
  ConversationId,
  Document,
  GraphDatabase,
  OrgId,
} from "@ctrl/controller-rpc";

import {
  getAgentName,
  getCustomAgentOverride,
} from "@/components/ai/useAgents";
import type { AiAttachment } from "@/components/ai/useAttachments";
import type { BreadcrumbItem } from "@/components/Breadcrumb.vue";
import { resolvePromptContext } from "@/components/editor/context";
import {
  documentToProseMirrorDoc,
  proseMirrorDocToDocument,
} from "@/components/editor/document-to-prosemirror";
import * as inject from "@/components/inject";
import type { BaseLogger as Logger } from "@/components/inject";
import { useDocumentMerge } from "@/composables/useDocumentMerge";
import type { UseProfile } from "@/composables/useProfile";
import { bindAsync, releasableRef, releaseBestEffort } from "@/ref";

type PromptField = "agent_instructions" | "user_preferences" | "working_memory";

function contextCounts(context: AiPromptContext | undefined) {
  return {
    integrations: context?.integrations?.length ?? 0,
    mentions: context?.mentions?.length ?? 0,
    references: context?.references?.length ?? 0,
  };
}

export type AgentChatSession = {
  send: (
    messageDoc: ProseMirrorNode,
    conversationId?: ConversationId,
    attachments?: AiAttachment[]
  ) => Promise<AiConversationRichResponse>;
};

export type UseAgentResult = {
  agent: Ref<{ id: string; name?: string; key?: string } | undefined>;
  agentInstructionsDocument: Ref<Document>;
  userPreferencesDocument: Ref<Document>;
  workingMemoryDocument: Ref<Document>;
  /** Combined context (integrations + references) from agent instructions and user preferences. */
  agentConfigContext: Ref<AiPromptContext>;
  handleAgentInstructionsChange: (
    pmDoc: ProseMirrorNode,
    shouldFlush?: boolean
  ) => void;
  handleUserPreferencesChange: (
    pmDoc: ProseMirrorNode,
    shouldFlush?: boolean
  ) => void;
  handleWorkingMemoryChange: (
    pmDoc: ProseMirrorNode,
    shouldFlush?: boolean
  ) => void;
  isLoading: Ref<boolean>;
  hierarchy: Ref<Array<BreadcrumbItem>>;
  /** Graph database for the agent (for CustomObjectTopBar). */
  db: Ref<GraphDatabase | undefined>;
  /** Row id for the agent (for CustomObjectTopBar). */
  id: Ref<string | undefined>;
  /** Resolved organization ID for the agent's workspace (required for integration tools). Never null. */
  organizationId: OrgId;
  /** Chat session: send() that calls controller RPC. Reusable outside Agent.vue. */
  chat: () => AgentChatSession;
  /** Currently active conversation ID. */
  conversationId: Ref<ConversationId | undefined>;
  /** List of conversation IDs for this agent. */
  conversationIds: Ref<ConversationId[]>;
  /**
   * Push any debounced document edits (5s debounce in `useDocumentMerge`) to
   * the controller now. `release()` also flushes, but the calling routed
   * component should additionally wire this to `onBeforeRouteLeave` —
   * registered in its setup before the first `await`, with the flush bound
   * once this composable resolves — so the controller has the latest content
   * before the next mount re-fetches the agent.
   */
  flushPendingEdits: () => void;
  /**
   * Release all resources owned by this composable (flush callbacks, agent
   * subscription). Idempotent.
   */
  release: () => void;
};

/**
 * Load the agent data for a single agent by agent ID.
 * Calls controller.agent.get() to fetch the full agent object reactively.
 *
 * @param activeConversationId Optional ref to override which conversation to load.
 *   If provided, that conversation's history is loaded instead of the first one.
 * @param source Names the calling component in log entries: several components
 *   call useAgent for the same agent (Agent, AgentConfiguration,
 *   AgentConversation), each creating its own set of document-merge instances,
 *   and their interleaved logs are unattributable without it.
 */
export async function useAgent(
  controller: Controller,
  _profile: Ref<UseProfile>,
  _logger: Logger,
  organizationId: Ref<OrgId>,
  agentId: Ref<string>,
  activeConversationId?: Ref<ConversationId | undefined>,
  source?: string
): Promise<UseAgentResult> {
  const parent = computed(() =>
    agentId.value
      ? ({ kind: "object" as const, id: agentId.value } as const)
      : null
  );
  provide("parent", parent);

  // Inject must happen synchronously before any await.
  const pendingChangeFlushes = inject.pendingChangeFlushes();
  const integrations = inject.integrations();
  const resolveConnectionOrgId = (connectionId: string) =>
    integrations.organizationIdForConnection(connectionId);

  // agentData is declared synchronously (undefined) so useDocumentMerge can be
  // called before await, then populated after the await. The document refs
  // stay undefined ("not loaded yet") until then: useDocumentMerge adopts the
  // first real document outright instead of merging it against an empty
  // placeholder.
  const agentData = shallowRef<AiAgent | null | undefined>(undefined);

  const agentInstructionsData = computed<Document | undefined>(
    () => agentData.value?.agent_instructions?.document
  );
  const userPreferencesData = computed<Document | undefined>(
    () => agentData.value?.user_preferences?.document
  );
  const workingMemoryData = computed<Document | undefined>(
    () => agentData.value?.working_memory?.document
  );

  const latestAgentInstructionsPmDoc = shallowRef<ProseMirrorNode | null>(null);
  const latestUserPreferencesPmDoc = shallowRef<ProseMirrorNode | null>(null);
  const latestWorkingMemoryPmDoc = shallowRef<ProseMirrorNode | null>(null);

  const logger = _logger.forModule("useAgent");
  const mergeLabel = (field: PromptField) =>
    `${source ?? "useAgent"}/${field}@${agentId.value}`;
  logger.info("useAgent created", { source, agentId: agentId.value });

  // Build the prompt persisted by a save, logging enough to diagnose why a
  // prompt's context ends up empty: the conversation context banner renders
  // primarily from the persisted context, so an empty save blanks it.
  const buildPromptForSave = async (
    field: PromptField,
    latestPmDoc: ProseMirrorNode | null,
    doc: Document
  ): Promise<AiPrompt> => {
    // When no editor produced a ProseMirror doc for this prompt this session,
    // derive one from the document being saved: persisting an empty context
    // instead would destroy whatever context was stored before.
    const pmDoc = latestPmDoc ?? documentToProseMirrorDoc(doc, _logger);
    const context = await resolvePromptContext(
      controller,
      pmDoc,
      ["integrations", "mentions", "references"],
      _logger,
      resolveConnectionOrgId
    );
    logger.info(
      latestPmDoc
        ? `saving ${field}`
        : `saving ${field} without editor state: derived context from the document`,
      {
        agentId: agentId.value,
        documentBlocks: doc.blocks.length,
        context: contextCounts(context),
      }
    );
    return { context, document: doc };
  };

  const {
    localDocument: agentInstructionsDocument,
    handleLocalChange: handleAgentInstructionsChangeInternal,
    flush: flushAgentInstructions,
    release: releaseAgentInstructionsMerge,
  } = useDocumentMerge(
    agentInstructionsData,
    controller,
    _logger,
    // Awaited so useDocumentMerge's mutex covers the whole save RPC
    // (see `editor-document-merging.md`, "Merging / push race condition").
    async (doc) => {
      await controller.ai.agent.update(organizationId.value, agentId.value!, {
        agent_instructions: await buildPromptForSave(
          "agent_instructions",
          latestAgentInstructionsPmDoc.value,
          doc
        ),
      });
    },
    { label: mergeLabel("agent_instructions") }
  );

  const {
    localDocument: userPreferencesDocument,
    handleLocalChange: handleUserPreferencesChangeInternal,
    flush: flushUserPreferences,
    release: releaseUserPreferencesMerge,
  } = useDocumentMerge(
    userPreferencesData,
    controller,
    _logger,
    async (doc) => {
      await controller.ai.agent.update(organizationId.value, agentId.value!, {
        user_preferences: await buildPromptForSave(
          "user_preferences",
          latestUserPreferencesPmDoc.value,
          doc
        ),
      });
    },
    { label: mergeLabel("user_preferences") }
  );

  const {
    localDocument: workingMemoryDocument,
    handleLocalChange: handleWorkingMemoryChangeInternal,
    flush: flushWorkingMemory,
    release: releaseWorkingMemoryMerge,
  } = useDocumentMerge(
    workingMemoryData,
    controller,
    _logger,
    async (doc) => {
      await controller.ai.agent.update(organizationId.value, agentId.value!, {
        working_memory: await buildPromptForSave(
          "working_memory",
          latestWorkingMemoryPmDoc.value,
          doc
        ),
      });
    },
    { label: mergeLabel("working_memory") }
  );

  const flushPendingEdits = () => {
    flushAgentInstructions();
    flushUserPreferences();
    flushWorkingMemory();
  };
  pendingChangeFlushes.add(flushPendingEdits);

  const handleAgentInstructionsChange = (
    pmDoc: ProseMirrorNode,
    shouldFlush?: boolean
  ) => {
    latestAgentInstructionsPmDoc.value = pmDoc;
    handleAgentInstructionsChangeInternal(
      proseMirrorDocToDocument(pmDoc),
      shouldFlush
    );
  };

  const handleUserPreferencesChange = (
    pmDoc: ProseMirrorNode,
    shouldFlush?: boolean
  ) => {
    latestUserPreferencesPmDoc.value = pmDoc;
    handleUserPreferencesChangeInternal(
      proseMirrorDocToDocument(pmDoc),
      shouldFlush
    );
  };

  const handleWorkingMemoryChange = (
    pmDoc: ProseMirrorNode,
    shouldFlush?: boolean
  ) => {
    latestWorkingMemoryPmDoc.value = pmDoc;
    handleWorkingMemoryChangeInternal(
      proseMirrorDocToDocument(pmDoc),
      shouldFlush
    );
  };

  const agentRefP = bindAsync(agentId, async (id: string | undefined) => {
    if (!id) return releasableRef<AiAgent | null>(null);
    return controller.ai.agent.get(organizationId.value, id);
  });

  const agentRef = await agentRefP;

  agentData.value = agentRef.value;
  const unwatchAgentData = watch(
    () => agentRef.value,
    (v) => {
      agentData.value = v;
    }
  );

  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    logger.info("useAgent released", { source, agentId: agentId.value });
    pendingChangeFlushes.delete(flushPendingEdits);
    flushPendingEdits();
    releaseAgentInstructionsMerge();
    releaseUserPreferencesMerge();
    releaseWorkingMemoryMerge();
    unwatchAgentData();
    releaseBestEffort(agentRef);
  };

  const agent = computed<
    { id: string; name?: string; key?: string } | undefined
  >(() => {
    const data = agentData.value;
    if (!data) return undefined;
    const catalogName = data.key ? getAgentName(data.key) : "";
    const overrideName = getCustomAgentOverride(data.id)?.name;
    return {
      id: data.id,
      name: data.name ?? (catalogName || undefined) ?? overrideName ?? "Agent",
      key: data.key ?? undefined,
    };
  });

  const conversationIds = computed<ConversationId[]>(
    () => agentData.value?.conversations ?? []
  );

  // Determine which conversation ID to load: use activeConversationId if provided, else first
  const conversationIdToLoad = computed<ConversationId | undefined>(() => {
    if (activeConversationId?.value) {
      return activeConversationId.value;
    }
    return conversationIds.value[0];
  });

  const isLoading = computed(
    () => agentData.value === undefined && agentId.value !== undefined
  );

  const hierarchy = ref<Array<BreadcrumbItem>>([]);
  const db = ref<GraphDatabase | undefined>(undefined);
  const id = ref<string | undefined>(agentId.value);

  function chat(): AgentChatSession {
    async function send(
      message: ProseMirrorNode,
      overrideConversationId?: ConversationId,
      attachments?: AiAttachment[]
    ): Promise<AiConversationRichResponse> {
      // Import here to avoid circular dependency
      const { serializeDoc } = await import("@/components/editor/serialize");
      const context = await resolvePromptContext(
        controller,
        message,
        ["integrations", "mentions", "references"],
        _logger,
        resolveConnectionOrgId
      );

      // Convert ProseMirror to markdown string
      const messageMarkdown = serializeDoc(message);

      if (!agentId.value) {
        throw new Error("[agent] missing agent ID");
      }

      // Call controller RPC to send message to agent
      const convId = overrideConversationId ?? conversationIdToLoad.value;
      if (!convId) {
        throw new Error("[agent] missing conversation ID");
      }

      const rpcAttachments: Attachment[] = (attachments ?? []).map((a) => ({
        created: new Date(),
        id: a.id,
        mime: a.mime,
        name: a.name,
        url: a.url,
      }));

      const result = await controller.ai.agent.conversation.sendRich(
        context,
        organizationId.value,
        convId,
        messageMarkdown,
        rpcAttachments
      );

      if ("error" in result) {
        const limit = result.error;
        const resetDate = limit.reset_date.toLocaleDateString();
        throw new Error(
          `AI usage limit reached. Your limit will reset on ${resetDate}.`
        );
      }
      return result.value;
    }

    return { send };
  }

  const agentConfigContext = computed<AiPromptContext>(() => {
    const ins: AiPromptContext =
      agentData.value?.agent_instructions?.context ?? {};
    const pref: AiPromptContext =
      agentData.value?.user_preferences?.context ?? {};
    return {
      integrations: [...(ins.integrations ?? []), ...(pref.integrations ?? [])],
      references: [...(ins.references ?? []), ...(pref.references ?? [])],
      mentions: [...(ins.mentions ?? []), ...(pref.mentions ?? [])],
    };
  });

  return {
    agent,
    agentInstructionsDocument,
    userPreferencesDocument,
    workingMemoryDocument,
    agentConfigContext,
    handleAgentInstructionsChange,
    handleUserPreferencesChange,
    handleWorkingMemoryChange,
    isLoading,
    hierarchy,
    db,
    id,
    organizationId: organizationId.value,
    chat,
    conversationId: conversationIdToLoad,
    conversationIds,
    flushPendingEdits,
    release,
  };
}
